import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LayoutDashboard, Video, FolderOpen, Award, User, LogOut, Play, Download,
  Lock, Search, FileText, Sparkles, LibraryBig, ShoppingCart, CheckCircle2
} from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { STUDENT } from '@/constants/testIds';
import { BRAND, MOCK_STUDENT } from '@/data/mockData';
import { useAuth } from '@/contexts/AuthContext';
import { api, formatApiError } from '@/lib/api';
import useRazorpayCheckout from '@/hooks/useRazorpayCheckout';

const NAV = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard, tid: STUDENT.navOverview },
  { key: 'enrollments', label: 'Enrollments', icon: Sparkles, tid: STUDENT.navEnrollments },
  { key: 'recordings', label: 'Session Recordings', icon: Video, tid: STUDENT.navRecordings },
  { key: 'resources', label: 'Resources', icon: FolderOpen, tid: STUDENT.navResources },
  { key: 'ebooks', label: 'eBook Library', icon: LibraryBig, tid: STUDENT.navEbooks },
  { key: 'certificates', label: 'Certificates', icon: Award, tid: STUDENT.navCertificates },
  { key: 'profile', label: 'Profile', icon: User, tid: STUDENT.navProfile },
];

const StudentDashboard = () => {
  const [view, setView] = useState('overview');
  const [activeVideo, setActiveVideo] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Persisted batch context — survives full page refresh per spec §7.
  const BATCH_STORAGE_KEY = 'bymat.selectedBatchId';
  const [selectedBatchId, setSelectedBatchId] = useState(() => {
    try { return sessionStorage.getItem(BATCH_STORAGE_KEY) || null; } catch { return null; }
  });
  // Use refs so reloadDashboard has a STABLE identity across state changes —
  // breaks the dep cascade that previously caused 5 racing fetches on dropdown switch.
  const selectedBatchIdRef = useRef(selectedBatchId);
  const userSelectedRef = useRef(!!selectedBatchId);

  // Stable reload — always reads the latest selectedBatchId from the ref.
  const reloadDashboard = useCallback(async (bid) => {
    try {
      const id = bid || selectedBatchIdRef.current;
      const url = id ? `/student/dashboard?batch_id=${id}` : '/student/dashboard';
      const { data } = await api.get(url);
      setDashboard(data);
      // If persisted selection is stale (e.g., student no longer enrolled in that batch),
      // server returns its own selected_batch_id — adopt it and re-persist.
      const serverPick = data.selected_batch_id;
      const availableIds = new Set((data.available_batches || []).map((b) => b.id));
      if (selectedBatchIdRef.current && !availableIds.has(selectedBatchIdRef.current) && serverPick) {
        selectedBatchIdRef.current = serverPick;
        setSelectedBatchId(serverPick);
        try { sessionStorage.setItem(BATCH_STORAGE_KEY, serverPick); } catch { /* ignore */ }
      }
      // ONLY adopt server-selected batch on the very first load (before user picked anything).
      if (!userSelectedRef.current && !selectedBatchIdRef.current && serverPick) {
        selectedBatchIdRef.current = serverPick;
        setSelectedBatchId(serverPick);
        try { sessionStorage.setItem(BATCH_STORAGE_KEY, serverPick); } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/student/me/enrollments');
        if (!cancelled) setEnrollments(data.items || []);
      } catch { /* ignore — show empty */ }
    })();
    reloadDashboard();

    // Auto-refetch dashboard whenever the tab regains focus or comes back online
    const onFocus = () => reloadDashboard();
    const onVisibility = () => { if (document.visibilityState === 'visible') reloadDashboard(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Refetch dashboard when the user navigates back to the Overview tab.
  // We use ref-based reload so this effect doesn't re-fire on every callback identity change.
  useEffect(() => {
    if (view === 'overview') reloadDashboard();
  }, [view]);

  // Compose a "student" object that merges real user info + first enrollment.
  const student = useMemo(() => {
    const firstEnroll = enrollments[0];
    const eb = dashboard?.enrolled_batch;
    return {
      ...MOCK_STUDENT,
      name: user?.full_name || MOCK_STUDENT.name,
      email: user?.email || MOCK_STUDENT.email,
      phone: user?.mobile || MOCK_STUDENT.phone,
      batchName: eb?.batch_name || firstEnroll?.batch?.batch_name || 'Not Assigned',
      enrolledOn: eb?.enrolled_on
        ? eb.enrolled_on.slice(0, 10)
        : (firstEnroll?.enrollment_date ? firstEnroll.enrollment_date.slice(0, 10) : '—'),
      hasEnrollment: enrollments.length > 0,
    };
  }, [user, enrollments, dashboard]);

  const onLogout = async () => {
    await logout();
    toast.success('Logged out');
    navigate('/learning-hub');
  };

  return (
    <div className="min-h-screen bg-[#050A14] text-white flex">
      <aside data-testid={STUDENT.sidebar} className="hidden lg:flex w-72 flex-col border-r border-white/5 bg-[#070d1c] sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-3 px-6 py-6 border-b border-white/5">
          <img src={BRAND.logoUrl} alt="BYMAT" className="h-10 w-10 object-contain rounded-lg bg-black" />
          <div>
            <div className="font-display font-black">Learning Hub</div>
            <div className="text-[10px] tracking-widest font-mono text-brand-cyan uppercase">Student Portal</div>
          </div>
        </Link>

        <div className="px-4 py-4 border-b border-white/5">
          <div className="glass rounded-xl p-3">
            <div className="text-[10px] tracking-widest font-mono text-brand-cyan uppercase">Enrolled Batch</div>
            {(dashboard?.available_batches?.length || 0) > 1 ? (
              <select
                data-testid="student-sidebar-batch-select"
                value={selectedBatchId || dashboard.selected_batch_id || ''}
                onChange={(e) => {
                  userSelectedRef.current = true;
                  selectedBatchIdRef.current = e.target.value;
                  setSelectedBatchId(e.target.value);
                  try { sessionStorage.setItem(BATCH_STORAGE_KEY, e.target.value); } catch { /* ignore */ }
                  reloadDashboard(e.target.value);
                }}
                className="mt-1 w-full bg-transparent font-semibold text-sm focus:outline-none border border-white/10 rounded-md px-2 py-1 hover:border-brand-cyan/40"
              >
                {dashboard.available_batches.map((b) => (
                  <option key={b.id} value={b.id} className="bg-[#0B1221]">{b.batch_name}</option>
                ))}
              </select>
            ) : (
              <div className="font-semibold text-sm mt-0.5 leading-tight" data-testid={STUDENT.sidebarBatchName}>{student.batchName}</div>
            )}
            <div className="mt-3">
              <div className="flex items-center justify-between text-[10px] mb-1 text-white/60"><span>Progress</span><span data-testid={STUDENT.sidebarProgressValue}>{dashboard?.progress_percentage ?? 0}%</span></div>
              <Progress value={dashboard?.progress_percentage ?? 0} className="h-1.5 bg-white/10" />
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((n) => (
            <button key={n.key} type="button" data-testid={n.tid} onClick={() => setView(n.key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                view === n.key ? 'bg-brand-cyan/10 text-brand-cyan border border-brand-cyan/20' : 'text-white/70 hover:bg-white/5'
              }`}>
              <n.icon className="w-4 h-4" />
              {n.label}
            </button>
          ))}
        </nav>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 glass-strong border-b border-white/5 flex items-center justify-between px-4 h-14">
        <div className="flex items-center gap-2">
          <img src={BRAND.logoUrl} alt="BYMAT" className="h-8 w-8 object-contain rounded bg-black" />
          <div className="font-display font-bold text-sm">Learning Hub</div>
        </div>
        {/* Logout moved to the main header (top-right). The mobile bar shows a small inline copy for reachability. */}
        <button type="button" onClick={onLogout} data-testid={STUDENT.logoutButton}
          className="text-xs text-white/80 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-white/15 hover:bg-white/10 hover:border-brand-cyan/40 transition">
          <LogOut className="w-3.5 h-3.5" /> Logout
        </button>
      </div>

      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        <div className="lg:hidden flex overflow-x-auto gap-2 px-4 py-3 border-b border-white/5">
          {NAV.map((n) => (
            <button key={n.key} type="button" onClick={() => setView(n.key)}
              className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs ${view === n.key ? 'bg-brand-cyan text-black' : 'glass'}`}>
              <n.icon className="w-3.5 h-3.5" /> {n.label}
            </button>
          ))}
        </div>

        <div className="p-5 lg:p-8 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase font-mono text-brand-cyan/80">Welcome back</div>
              <h1 className="font-display text-2xl sm:text-3xl font-black mt-1">{student.name}</h1>
            </div>
            <div className="flex flex-col items-end gap-2">
              {/* Desktop Logout — top-right, above the batch chip per design spec */}
              <button
                type="button"
                onClick={onLogout}
                data-testid="student-header-logout-button"
                className="hidden lg:inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-md border border-white/15 text-white/80 hover:bg-white/10 hover:border-brand-cyan/40 hover:text-white transition cursor-pointer"
                title="Sign out of the Student Portal"
              >
                <LogOut className="w-3.5 h-3.5" /> Logout
              </button>
              <div className="text-sm text-white/60 inline-flex items-center gap-2 glass px-3 py-1.5 rounded-full">
                <Sparkles className="w-3.5 h-3.5 text-brand-cyan" /> {student.batchName}
              </div>
            </div>
          </div>

          {view === 'overview' && (
            <Overview
              dashboard={dashboard}
              onResume={async (cw) => {
                if (!cw) return;
                try {
                  const { data } = await api.get(`/student/videos/${cw.id}/playback`);
                  setActiveVideo({ id: cw.id, title: cw.title, duration: cw.duration, session_date: cw.session_date, ...data });
                } catch { setActiveVideo({ id: cw.id, title: cw.title, duration: cw.duration, session_date: cw.session_date }); }
                setView('recordings');
              }}
            />
          )}
          {view === 'recordings' && <Recordings activeVideo={activeVideo} setActiveVideo={setActiveVideo} student={student} onProgress={reloadDashboard} batchId={selectedBatchId || dashboard?.selected_batch_id || null} />}
          {view === 'enrollments' && <EnrollmentsView reloadDashboard={reloadDashboard} />}
          {view === 'resources' && <Resources batchId={selectedBatchId || dashboard?.selected_batch_id || null} />}
          {view === 'ebooks' && <EbookLibrary />}
          {view === 'certificates' && <Certificates batchId={selectedBatchId || dashboard?.selected_batch_id || null} />}
          {view === 'profile' && <Profile student={student} />}
        </div>
      </main>
    </div>
  );
};

const Overview = ({ dashboard, onResume }) => {
  const cw = dashboard?.continue_watching;
  const sessionsTotal = dashboard?.sessions_total ?? 0;
  const sessionsWatched = dashboard?.sessions_watched ?? 0;
  const resumeMin = cw ? Math.floor((cw.last_position_seconds || 0) / 60) : 0;
  const watchedPct = cw ? Math.round(cw.watched_percentage || 0) : 0;
  const latest = dashboard?.latest_resources || [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Sessions Watched" value={`${String(sessionsWatched).padStart(2, '0')} / ${String(sessionsTotal).padStart(2, '0')}`} testId={STUDENT.statSessionsWatched} />
        <Stat label="Resources Downloaded" value={String(dashboard?.resources_downloaded ?? 0).padStart(2, '0')} testId={STUDENT.statResourcesDownloaded} />
        <Stat label="Hours Learned" value={`${dashboard?.hours_learned ?? 0}h`} testId={STUDENT.statHoursLearned} />
        <Stat label="Certificate Status" value={dashboard?.certificate_status || '—'} cyan testId={STUDENT.statCertificateStatus} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 glass rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-bold">Continue watching</div>
            {cw && (
              <button type="button" data-testid={STUDENT.continueWatchingResume} onClick={() => onResume(cw)} className="text-xs text-brand-cyan inline-flex items-center gap-1 hover:underline">Resume →</button>
            )}
          </div>
          {cw ? (
            <div className="relative rounded-xl overflow-hidden aspect-video bg-black">
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-brand-cyan/10 to-black">
                <Video className="w-14 h-14 text-brand-cyan/30" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <button type="button" data-testid={STUDENT.continueWatchingPlay} onClick={() => onResume(cw)} className="absolute inset-0 flex items-center justify-center">
                <span className="h-14 w-14 rounded-full bg-brand-cyan text-black flex items-center justify-center btn-glow">
                  <Play className="w-6 h-6 ml-1" fill="currentColor" />
                </span>
              </button>
              <div className="absolute bottom-3 left-4 right-4">
                <div className="font-semibold" data-testid={STUDENT.continueWatchingTitle}>{cw.title}</div>
                <div className="text-xs text-white/70">
                  {cw.duration ? `${cw.duration} · ` : ''}
                  {(cw.last_position_seconds || 0) > 0 ? `Resume at ${resumeMin}m` : 'Start watching'}
                  {watchedPct > 0 ? ` · ${watchedPct}% watched` : ''}
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-white/10 p-10 text-center">
              <Video className="w-8 h-8 text-brand-cyan/50 mx-auto" />
              <div className="mt-3 font-semibold">Nothing to resume yet</div>
              <p className="mt-2 text-sm text-white/60 max-w-md mx-auto">Once your trainer uploads session recordings, they will appear here.</p>
            </div>
          )}
        </div>
        <div className="glass rounded-2xl p-5" data-testid={STUDENT.latestResourcesList}>
          <div className="font-display font-bold mb-4">Latest Resources</div>
          {latest.length === 0 ? (
            <div className="text-sm text-white/60 py-6 text-center">No resources uploaded yet.</div>
          ) : (
            <ul className="space-y-3">
              {latest.map((r) => (
                <li key={r.id} className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-brand-cyan" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.title}</div>
                    <div className="text-[11px] text-white/50">{(r.file_type || '').split('/').pop() || '—'} · {r.file_size || '—'}</div>
                  </div>
                  {r.is_downloadable ? <Download className="w-4 h-4 text-white/50" /> : <Lock className="w-4 h-4 text-red-400/70" />}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, cyan, testId }) => (
  <div className="glass rounded-2xl p-5" data-testid={testId}>
    <div className="text-[10px] tracking-widest uppercase font-mono text-white/50">{label}</div>
    <div className={`font-display text-2xl font-black mt-2 ${cyan ? 'text-brand-cyan' : ''}`}>{value}</div>
  </div>
);

const EnrollmentsView = ({ reloadDashboard }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [failed, setFailed] = useState({}); // batch_id → true when last attempt failed
  const { launch: launchRazorpay, ready: rzpReady } = useRazorpayCheckout();

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/student/available-batches');
      setItems(data.items || []);
    } catch { /* keep stale */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') { refresh(); reloadDashboard?.(); } };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => { document.removeEventListener('visibilitychange', onVis); window.removeEventListener('focus', onVis); };
  }, [refresh, reloadDashboard]);

  const enroll = async (b) => {
    if (b.is_enrolled || acting) return;
    if (!b.course_id || !b.course_fee) {
      toast.error('This batch is not yet configured for payments — please contact admin');
      return;
    }
    if (!rzpReady) { toast.info('Payment popup loading, please try again in a moment...'); return; }
    setActing(b.id);
    setFailed((p) => { const n = { ...p }; delete n[b.id]; return n; });
    await launchRazorpay({
      courseId: b.course_id,
      batchId: b.id,
      amountInr: b.course_fee,
      courseName: `${b.course_name || 'Course'} — ${b.batch_name}`,
      onSuccess: async () => {
        await refresh();
        reloadDashboard?.();
        setActing(null);
      },
      onFailure: () => {
        setFailed((p) => ({ ...p, [b.id]: true }));
        setActing(null);
      },
    });
  };

  const inr = (v) => v == null ? '—' : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

  if (loading) {
    return <div className="text-sm text-white/50 py-10 text-center">Loading available batches...</div>;
  }
  if (items.length === 0) {
    return (
      <div className="glass rounded-2xl p-10 text-center text-sm text-white/60">
        No batches available right now. Please check back later.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="student-enrollments-view">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-bold">Available Batches · {items.length}</h2>
        <span className="text-[11px] tracking-widest font-mono uppercase text-white/40">Secure checkout · Razorpay</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {items.map((b) => {
          const status = (b.status || 'Active');
          const isCompleted = status.toLowerCase() === 'completed';
          const isEnrolled = b.is_enrolled;
          const isPending = !isEnrolled && (b.payment_status === 'pending');
          const lastFailed = !!failed[b.id];
          const noPayment = !b.payment_link && !b.course_fee;
          const isActing = acting === b.id;
          const disabled = isEnrolled || isActing || noPayment;
          let label = 'Enroll Now';
          let toneClass = 'bg-brand-cyan text-black btn-glow hover:bg-brand-cyanSoft';
          if (isEnrolled) { label = 'Enrolled'; toneClass = 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 cursor-not-allowed'; }
          else if (isActing) { label = 'Processing...'; toneClass = 'bg-brand-cyan/60 text-black cursor-wait'; }
          else if (lastFailed) { label = 'Retry Payment'; toneClass = 'bg-red-500/20 text-red-300 border border-red-400/40 hover:bg-red-500/30'; }
          else if (isPending) { label = 'Payment Processing'; toneClass = 'bg-amber-500/20 text-amber-300 border border-amber-400/30 cursor-not-allowed'; }
          else if (noPayment) { label = 'Coming Soon'; toneClass = 'bg-white/5 text-white/40 cursor-not-allowed'; }
          return (
            <div key={b.id} data-testid={`enrollment-batch-card-${b.id}`} className="glass rounded-2xl p-5 flex flex-col">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <div className="text-[10px] tracking-widest font-mono text-brand-cyan uppercase">{b.course_name || 'Course'}</div>
                  <div className="font-display font-bold text-lg mt-1 truncate" title={b.batch_name}>{b.batch_name}</div>
                </div>
                <span className={`shrink-0 text-[10px] tracking-widest font-mono px-2 py-1 rounded-full ${isCompleted ? 'bg-amber-500/10 text-amber-300' : 'bg-emerald-500/10 text-emerald-400'}`}>
                  {status}
                </span>
              </div>

              <dl className="grid grid-cols-2 gap-3 text-xs text-white/70 mb-4">
                <div>
                  <dt className="text-white/40">Fee</dt>
                  <dd className="font-mono text-white" data-testid={`enrollment-fee-${b.id}`}>{inr(b.course_fee)}</dd>
                </div>
                <div>
                  <dt className="text-white/40">Sessions</dt>
                  <dd className="font-mono text-white">{b.total_sessions}</dd>
                </div>
                <div>
                  <dt className="text-white/40">Start</dt>
                  <dd className="font-mono text-white">{b.start_date ? b.start_date.slice(0, 10) : '—'}</dd>
                </div>
                <div>
                  <dt className="text-white/40">Trainer</dt>
                  <dd className="font-mono text-white truncate">{b.trainer_name || '—'}</dd>
                </div>
              </dl>

              <button
                type="button"
                data-testid={`enrollment-cta-${b.id}`}
                disabled={disabled}
                onClick={() => enroll(b)}
                className={`mt-auto w-full py-2.5 rounded-lg font-semibold text-sm transition inline-flex items-center justify-center gap-2 ${toneClass}`}
              >
                {isActing && <span className="inline-block w-3.5 h-3.5 border-2 border-current border-r-transparent rounded-full animate-spin" />}
                {label}
              </button>
              {isPending && (
                <button
                  type="button"
                  data-testid={`enrollment-verify-${b.id}`}
                  onClick={async () => {
                    try {
                      await api.post('/student/payments/verify-pending', { batch_id: b.id });
                      toast.success('Payment verified — access granted!');
                      refresh();
                      reloadDashboard?.();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Payment is still being processed. Please wait a few moments.');
                    }
                  }}
                  className="mt-2 w-full text-[11px] text-brand-cyan hover:underline tracking-wider uppercase font-mono"
                >
                  I&apos;ve completed payment — verify now
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Recordings = ({ activeVideo, setActiveVideo, student, onProgress, batchId }) => {
  const [search, setSearch] = useState('');
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setVideos([]); // clear stale items so previous-batch videos don't flash while new fetch is inflight
    const url = batchId ? `/student/videos?batch_id=${batchId}` : '/student/videos';
    api.get(url).then((r) => setVideos(r.data.items || []))
      .catch(() => setVideos([])).finally(() => setLoading(false));
  }, [batchId]);

  const openVideo = async (v) => {
    try {
      const { data } = await api.get(`/student/videos/${v.id}/playback`);
      setActiveVideo({ ...v, ...data });
    } catch (e) { /* fall back to thumbnail-only */ setActiveVideo(v); }
  };

  const filtered = useMemo(
    () => videos.filter((v) => v.title.toLowerCase().includes(search.toLowerCase())),
    [videos, search]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <h2 className="font-display text-xl font-bold">Session Recordings · {videos.length}</h2>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search recordings..."
            className="pl-9 pr-4 py-2 text-sm rounded-lg bg-white/5 border border-white/10 focus:border-brand-cyan/50 focus:outline-none" />
        </div>
      </div>

      {activeVideo && <VideoPlayer video={activeVideo} student={student} onClose={() => { setActiveVideo(null); onProgress?.(); }} onProgress={onProgress} />}

      {loading ? (
        <div className="text-center py-10 text-white/50 text-sm">Loading recordings...</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/10 p-10 text-center">
          <Video className="w-8 h-8 text-brand-cyan/50 mx-auto" />
          <div className="mt-3 font-semibold">No recordings available yet</div>
          <p className="mt-2 text-sm text-white/60 max-w-md mx-auto">Your trainer will upload session recordings here after each live class. Make sure your enrollment is active.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((v) => (
            <button type="button" key={v.id} data-testid={`${STUDENT.videoCard}-${v.id}`} onClick={() => openVideo(v)}
              className="group glass rounded-xl overflow-hidden text-left hover:border-brand-cyan/40 transition">
              <div className="relative aspect-video bg-black flex items-center justify-center">
                <Video className="w-12 h-12 text-brand-cyan/40" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="h-12 w-12 rounded-full bg-brand-cyan/90 text-black flex items-center justify-center">
                    <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
                  </span>
                </div>
              </div>
              <div className="p-4">
                <div className="font-semibold text-sm leading-snug">{v.title}</div>
                <div className="text-xs text-white/50 mt-1">{v.duration || ''} {v.session_date ? '· ' + v.session_date.slice(0,10) : ''}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const VideoPlayer = ({ video, student, onClose, onProgress }) => {
  const [pos, setPos] = useState({ top: 20, left: 20, visible: true });
  const iframeRef = useRef(null);

  // Watermark drifter — cycles between 5 random zones every 5 seconds
  // Zones: top-left, top-right, center, bottom-left, bottom-right
  useEffect(() => {
    const zones = [
      { top: 5, left: 4 },     // top-left
      { top: 5, left: 70 },    // top-right
      { top: 45, left: 38 },   // center-ish
      { top: 85, left: 4 },    // bottom-left
      { top: 85, left: 70 },   // bottom-right
    ];
    const pickZone = () => {
      const z = zones[Math.floor(Math.random() * zones.length)];
      // Small jitter within zone so the watermark never sits perfectly still
      setPos({
        top: z.top + (Math.random() * 6 - 3),
        left: z.left + (Math.random() * 6 - 3),
        visible: true,
      });
    };
    pickZone();
    const i = setInterval(pickZone, 5000);
    return () => clearInterval(i);
  }, []);

  // Watch-progress reporter — uses Bunny iframe Player.js protocol.
  // Bunny's iframe.mediadelivery.net player follows player.js spec: subscribe via
  //   postMessage({ method:'addEventListener', value:'<eventName>' })
  // then receives { event:'<name>', value:<payload> } messages.
  useEffect(() => {
    if (!video?.id) return undefined;
    let currentTime = 0;
    let duration = 0;
    let lastReportedSeconds = -10;
    let ready = false;

    const post = (body) => api.post(`/student/videos/${video.id}/progress`, body).catch(() => {});

    // Initial "engagement" marker so the dashboard reflects that the video was opened
    post({ watched_seconds: 0, last_position_seconds: 0, watched_percentage: 0 });

    const sendToPlayer = (method, value) => {
      const w = iframeRef.current && iframeRef.current.contentWindow;
      if (!w) return;
      try { w.postMessage(JSON.stringify({ method, value }), '*'); }
      catch { /* ignore */ }
    };

    const subscribe = () => {
      ['ready', 'play', 'pause', 'timeupdate', 'ended', 'seeked'].forEach((evt) => {
        sendToPlayer('addEventListener', evt);
      });
      // Ask for the current duration up-front
      sendToPlayer('getDuration', null);
    };

    const onMsg = (event) => {
      // Accept any origin — Bunny embed lives on iframe.mediadelivery.net but we only act on shape.
      let data = event.data;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch { return; }
      }
      if (!data || typeof data !== 'object') return;

      const evt = data.event;
      const val = data.value;

      // Some players echo a "ready" event, others use "loaded" — handle both.
      if (evt === 'ready' || evt === 'loaded') {
        ready = true;
        sendToPlayer('getDuration', null);
        return;
      }

      // Duration response — Player.js returns { event:'getDuration', value: <number> } OR
      // bundles it inside timeupdate as { value: { duration, seconds } }.
      if (evt === 'getDuration' && typeof val === 'number') {
        duration = Math.floor(val);
        return;
      }

      if (evt === 'timeupdate') {
        // Bunny Player.js payload: { event:'timeupdate', value:{ seconds, duration } }
        if (val && typeof val === 'object') {
          if (typeof val.seconds === 'number') currentTime = Math.floor(val.seconds);
          if (typeof val.duration === 'number' && val.duration) duration = Math.floor(val.duration);
        } else if (typeof val === 'number') {
          currentTime = Math.floor(val);
        }
        // Throttle reports to avoid hammering the API (every ~10s of progress)
        if (Math.abs(currentTime - lastReportedSeconds) >= 10) {
          lastReportedSeconds = currentTime;
          const pct = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
          post({ watched_seconds: currentTime, last_position_seconds: currentTime, watched_percentage: pct });
        }
        return;
      }

      if (evt === 'ended') {
        const totalSec = duration || currentTime || video.duration_seconds || 0;
        post({
          watched_seconds: totalSec,
          last_position_seconds: totalSec,
          watched_percentage: 100,
          is_completed: true,
        }).then(() => onProgress?.());
        return;
      }
    };

    window.addEventListener('message', onMsg);

    // Subscribe whenever the iframe finishes loading. Player needs a tick before it can accept messages.
    const iframe = iframeRef.current;
    const onLoad = () => { setTimeout(subscribe, 300); };
    if (iframe) iframe.addEventListener('load', onLoad);
    // Also poll-subscribe for the first few seconds in case 'load' was missed
    const retry = setInterval(() => { if (!ready) subscribe(); else clearInterval(retry); }, 1000);
    setTimeout(() => clearInterval(retry), 8000);

    return () => {
      window.removeEventListener('message', onMsg);
      clearInterval(retry);
      if (iframe) iframe.removeEventListener('load', onLoad);
      // Final flush on unmount
      if (currentTime > 0) {
        const pct = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
        post({
          watched_seconds: currentTime,
          last_position_seconds: currentTime,
          watched_percentage: pct,
          is_completed: pct >= 90,
        }).then(() => onProgress?.());
      }
    };
  }, [video?.id, video?.duration_seconds, onProgress]);

  const watermarkText = `BYMAT Automation | ${student.email}`;
  // Random watermark drift positions — keeps it away from corners by using mid-range %.
  // Existing `pos` state already updates every 8s above; we keep that timing untouched.

  const shellRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Track fullscreen state across all vendor prefixes so the watermark stays mounted
  useEffect(() => {
    const onFsChange = () => {
      const el = document.fullscreenElement
        || document.webkitFullscreenElement
        || document.mozFullScreenElement
        || document.msFullscreenElement;
      setIsFullscreen(!!el);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    document.addEventListener('mozfullscreenchange', onFsChange);
    document.addEventListener('MSFullscreenChange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
      document.removeEventListener('mozfullscreenchange', onFsChange);
      document.removeEventListener('MSFullscreenChange', onFsChange);
    };
  }, []);

  const toggleFullscreen = async () => {
    const el = shellRef.current;
    if (!el) return;
    const inFs = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
    try {
      if (!inFs) {
        const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
        if (req) await req.call(el);
      } else {
        const exit = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
        if (exit) await exit.call(document);
      }
    } catch { /* user gesture missing — ignore */ }
  };

  return (
    <div className="glass-strong rounded-2xl overflow-hidden">
      <div
        ref={shellRef}
        className="video-shell relative aspect-video bg-black"
        onContextMenu={(e) => e.preventDefault()}
      >
        {video.iframe_url ? (
          <iframe
            ref={iframeRef}
            src={video.iframe_url}
            title={video.title}
            // Block PiP + force-iframe-fullscreen. Container-level fullscreen is handled
            // by our own button so the watermark always stays mounted.
            // `picture-in-picture` and `fullscreen` intentionally omitted from `allow`.
            allow="accelerometer; autoplay; encrypted-media; gyroscope"
            className="absolute inset-0 w-full h-full border-0"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="h-16 w-16 rounded-full bg-brand-cyan text-black flex items-center justify-center btn-glow">
              <Play className="w-7 h-7 ml-1" fill="currentColor" />
            </span>
          </div>
        )}
        {pos.visible && (
          <div
            className="watermark"
            data-testid="student-video-watermark"
            style={{ top: `${pos.top}%`, left: `${pos.left}%` }}
          >
            {watermarkText}
          </div>
        )}
        <div className="absolute top-3 right-3 z-10 flex gap-2">
          <button
            type="button"
            data-testid="student-mark-complete-button"
            onClick={async () => {
              try {
                await api.post(`/student/videos/${video.id}/complete`);
                toast.success('Marked as completed');
                onProgress?.();
              } catch {
                toast.error('Could not mark as completed');
              }
            }}
            className="text-xs px-3 py-1.5 rounded-md bg-brand-cyan/15 hover:bg-brand-cyan/25 text-brand-cyan border border-brand-cyan/40 font-mono uppercase tracking-widest"
          >
            Mark complete
          </button>
          <button
            type="button"
            data-testid="student-fullscreen-button"
            onClick={toggleFullscreen}
            className="text-xs glass px-3 py-1.5 rounded-md hover:bg-white/10 font-mono uppercase tracking-widest"
            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            {isFullscreen ? 'Exit FS' : 'Fullscreen'}
          </button>
          <button type="button" onClick={onClose} className="text-xs glass px-3 py-1.5 rounded-md hover:bg-white/10">Close</button>
        </div>
      </div>
      <div className="p-4 flex items-center justify-between">
        <div>
          <div className="font-semibold">{video.title}</div>
          <div className="text-xs text-white/50">{video.duration || ''} {video.session_date ? '· ' + video.session_date.slice(0,10) : ''}</div>
        </div>
        <div className="text-[10px] font-mono text-brand-cyan/80 tracking-widest uppercase">Secure Playback · Watermarked</div>
      </div>
    </div>
  );
};

const Resources = ({ batchId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setItems([]); // clear stale resources so previous-batch rows don't flash while new fetch is inflight
    const url = batchId ? `/student/resources?batch_id=${batchId}` : '/student/resources';
    api.get(url).then((r) => setItems(r.data.items || []))
      .catch(() => setItems([])).finally(() => setLoading(false));
  }, [batchId]);

  const download = async (r) => {
    try {
      const { data } = await api.get(`/student/resources/${r.id}/download-url`);
      // Open in new tab to trigger download
      window.open(data.download_url, '_blank');
      toast.success(`Downloading ${data.file_name}`);
    } catch (err) {
      toast.error('Download failed — please try again');
    }
  };

  return (
    <div className="glass rounded-2xl overflow-hidden">
      <div className="p-5 border-b border-white/5"><div className="font-display font-bold">Downloadable Resources · {items.length}</div></div>
      {loading ? (
        <div className="p-10 text-center text-white/50 text-sm">Loading resources...</div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center">
          <FileText className="w-8 h-8 text-brand-cyan/50 mx-auto" />
          <div className="mt-3 font-semibold">No resources available yet</div>
          <p className="mt-2 text-sm text-white/60 max-w-md mx-auto">Your trainer will share course materials, code samples, and PDFs here as the cohort progresses.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] tracking-widest uppercase font-mono text-white/50 border-b border-white/5 bg-white/2">
                <th className="py-3 px-5">Title</th><th className="py-3 px-5">Type</th><th className="py-3 px-5">Size</th>
                <th className="py-3 px-5">Date</th><th className="py-3 px-5 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-white/5">
                  <td className="py-3 px-5 font-semibold flex items-center gap-3"><FileText className="w-4 h-4 text-brand-cyan" /> {r.title}</td>
                  <td className="py-3 px-5 text-brand-cyan font-mono text-xs">{(r.file_type || '').split('/').pop() || '—'}</td>
                  <td className="py-3 px-5 text-white/70">{r.file_size || '—'}</td>
                  <td className="py-3 px-5 text-white/70 font-mono text-xs">{r.created_at ? r.created_at.slice(0,10) : '—'}</td>
                  <td className="py-3 px-5 text-right">
                    {r.is_downloadable ? (
                      <button type="button" data-testid={`${STUDENT.resourceDownload}-${r.id}`} onClick={() => download(r)}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-cyan hover:text-brand-cyanSoft">
                        <Download className="w-3.5 h-3.5" /> Download
                      </button>
                    ) : (
                      <span className="text-xs text-red-400/80 inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Locked</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const EbookLibrary = () => {
  const { user } = useAuth();
  const { launch } = useRazorpayCheckout();
  const [catalog, setCatalog] = useState({ ebooks: [], bundles: [] });
  const [loading, setLoading] = useState(true);
  const [purchaseSuccessOpen, setPurchaseSuccessOpen] = useState(false);
  const [purchaseSuccessMessage, setPurchaseSuccessMessage] = useState('Congratulations, your bundle purchase is successful. You can download the eBook from Premium eBook section now.');
  const [highlightedEbookIds, setHighlightedEbookIds] = useState([]);
  const handledIndividualPayments = useRef(new Set());

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/student/ebooks');
      setCatalog({ ebooks: data.ebooks || [], bundles: data.bundles || [] });
    } catch (err) {
      toast.error('Unable to load the eBook Library');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const download = async (ebook) => {
    try {
      const { data } = await api.get(`/student/ebooks/${ebook.id}/download-url`);
      window.open(data.download_url, '_blank');
      toast.success(`Downloading ${ebook.title}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const buy = (product, bundle = false) => launch({
    ebookId: bundle ? undefined : product.id,
    bundleId: bundle ? product.id : undefined,
    amountInr: product.price,
    courseName: product.title,
    onSuccess: async (paymentResult) => {
      if (!['captured', 'already_captured'].includes(paymentResult?.status)) return;
      if (bundle) {
        setHighlightedEbookIds((product.items || []).map((ebook) => ebook.id));
        setPurchaseSuccessMessage('Congratulations, your bundle purchase is successful. You can download the eBook from Premium eBook section now.');
        setPurchaseSuccessOpen(true);
      } else {
        const paymentId = paymentResult.payment?.id || `${product.id}:${paymentResult.status}`;
        if (handledIndividualPayments.current.has(paymentId)) return;
        handledIndividualPayments.current.add(paymentId);
        await load();
        setCatalog((current) => ({
          ...current,
          ebooks: current.ebooks.map((ebook) => ebook.id === product.id ? { ...ebook, entitled: true } : ebook),
        }));
        setHighlightedEbookIds([product.id]);
        setPurchaseSuccessMessage('Congratulations, your eBook purchase is successful. You can download the eBook from Premium eBook now.');
        setPurchaseSuccessOpen(true);
      }
      if (bundle) await load();
    },
  });

  if (loading) return <div className="glass rounded-2xl p-10 text-center text-white/60">Loading eBook Library...</div>;
  return (
    <div className="space-y-8">
      <Dialog open={purchaseSuccessOpen} onOpenChange={setPurchaseSuccessOpen}>
        <DialogContent
          className="bg-[#0B1221] border-brand-cyan/30 text-white max-w-md"
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto sm:mx-0" />
            <DialogTitle className="font-display text-xl">Congratulations!</DialogTitle>
          </DialogHeader>
          <p className="text-white/75 leading-relaxed">
            {purchaseSuccessMessage}
          </p>
          <DialogFooter>
            <button type="button" onClick={() => setPurchaseSuccessOpen(false)} className="bg-brand-cyan text-black font-semibold px-5 py-2.5 rounded-lg btn-glow hover:bg-brand-cyanSoft transition">
              Close
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div><div className="text-xs tracking-[0.3em] uppercase font-mono text-brand-cyan/80">Digital collection</div><h2 className="font-display text-2xl sm:text-3xl font-black mt-1">eBook Library</h2></div>
      <EbookSection title="Free e-Books" items={catalog.ebooks.filter((book) => book.is_free)} renderAction={(book) => <button type="button" onClick={() => download(book)} className="inline-flex items-center gap-2 text-xs font-semibold text-brand-cyan"><Download className="w-3.5 h-3.5" /> Download</button>} />
      <EbookSection title="Premium e-Books" items={catalog.ebooks.filter((book) => !book.is_free)} renderAction={(book) => book.entitled ? <button type="button" onClick={() => download(book)} className={`inline-flex items-center gap-2 text-xs font-semibold text-brand-cyan ${highlightedEbookIds.includes(book.id) ? 'btn-glow animate-pulse rounded-md px-2 py-1' : ''}`}><Download className="w-3.5 h-3.5" /> Download</button> : <button type="button" onClick={() => buy(book)} className="inline-flex items-center gap-2 text-xs font-semibold text-brand-cyan border border-brand-cyan/40 rounded-md px-2 py-1 btn-glow animate-pulse"><ShoppingCart className="w-3.5 h-3.5" /> Buy Now</button>} />
      <EbookSection title="Bundle e-Books" items={catalog.bundles} bundle renderAction={(bundle) => bundle.entitled ? <span className="inline-flex items-center gap-2 text-xs font-semibold text-emerald-400">Purchased</span> : <button type="button" onClick={() => buy(bundle, true)} className="inline-flex items-center gap-2 text-xs font-semibold text-brand-cyan border border-brand-cyan/40 rounded-md px-2 py-1 btn-glow animate-pulse"><ShoppingCart className="w-3.5 h-3.5" /> Buy Bundle</button>} />
    </div>
  );
};

const EbookSection = ({ title, items, bundle, renderAction }) => (
  <section className={`rounded-2xl border p-4 sm:p-5 ${title === 'Free e-Books' ? 'border-emerald-400/25 bg-emerald-400/[0.025]' : title === 'Premium e-Books' ? 'border-brand-cyan/25 bg-brand-cyan/[0.025]' : 'border-amber-300/25 bg-amber-300/[0.025]'}`}>
    <div className="flex items-center justify-between mb-4"><h3 className={`font-display text-lg font-bold ${title === 'Free e-Books' ? 'text-emerald-300' : title === 'Premium e-Books' ? 'text-brand-cyan' : 'text-amber-200'}`}>{title}</h3><span className="text-xs text-white/55">{items.length} available</span></div>
    {items.length === 0 ? <div className="glass rounded-2xl p-8 text-center text-sm text-white/50">No titles available yet.</div> : <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">{items.map((item) => <article key={item.id} className="glass rounded-2xl overflow-hidden flex flex-col"><div className="aspect-[3/2] bg-white/5 flex items-center justify-center">{item.cover_url ? <img src={item.cover_url} alt={`${item.title} cover`} className="w-full h-full object-cover" onError={(event) => { event.currentTarget.style.display = 'none'; event.currentTarget.nextElementSibling.style.display = 'block'; }} /> : null}<LibraryBig className={`w-10 h-10 text-brand-cyan/40 ${item.cover_url ? 'hidden' : ''}`} /></div><div className="p-5 flex-1 flex flex-col"><div className="text-[10px] tracking-widest uppercase font-mono text-brand-cyan">{bundle ? `${item.items?.length || 0} titles` : item.is_free ? 'Free' : <ProductPrice product={item} highlight={!item.entitled} />}</div><h4 className="font-display font-bold mt-2">{item.title}</h4><p className="text-sm text-white/60 mt-2 line-clamp-3">{item.description || 'A practical BYMAT learning resource.'}</p>{bundle && <p className="text-xs text-white/45 mt-3">Includes: {(item.items || []).map((book) => book.title).join(', ')}</p>}{bundle && <ProductPrice product={item} highlight={!item.entitled} /> }<div className="mt-5">{renderAction(item)}</div></div></article>)}</div>}
  </section>
);

const ProductPrice = ({ product, highlight = false }) => {
  const price = Number(product.price || 0);
  const originalPrice = Number(product.original_price || 0);
  const hasDiscount = price > 0 && originalPrice > price;
  const discountPercentage = hasDiscount ? Math.round(((originalPrice - price) / originalPrice) * 100) : 0;

  if (price <= 0) return <span>Free</span>;
  return (
    <span className="inline-flex items-center gap-2 normal-case tracking-normal font-sans text-sm">
      {hasDiscount && <span className="text-white/45 line-through">₹{originalPrice.toLocaleString('en-IN')}</span>}
      <span className="font-bold text-white">₹{price.toLocaleString('en-IN')}</span>
      {hasDiscount && <span className={`rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-300 ${highlight ? 'btn-glow animate-pulse' : ''}`}>{discountPercentage}% OFF</span>}
    </span>
  );
};

const Certificates = ({ batchId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setItems([]); // clear stale certificates so previous-batch entries don't flash while new fetch is inflight
    const url = batchId ? `/student/certificates?batch_id=${batchId}` : '/student/certificates';
    api.get(url).then((r) => setItems(r.data.items || []))
      .catch(() => setItems([])).finally(() => setLoading(false));
  }, [batchId]);

  if (loading) {
    return (
      <div className="glass rounded-2xl p-10 text-center text-sm text-white/50" data-testid="student-certificates-loading">
        Loading certificates...
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="glass rounded-2xl p-8 text-center" data-testid="student-certificates-empty">
        <Award className="w-12 h-12 text-brand-cyan mx-auto" />
        <h3 className="mt-4 font-display text-2xl font-bold">No Certificate Available</h3>
        <p className="mt-2 text-white/65 max-w-md mx-auto">
          Complete 90% of the batch progress + submit feedback to claim your BYMAT Automation course completion certificate.
        </p>
        <Link to="/feedback" className="mt-6 inline-flex items-center gap-2 bg-brand-cyan text-black font-semibold px-5 py-2.5 rounded-lg btn-glow hover:bg-brand-cyanSoft transition">
          Go to Feedback &amp; Claim
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="student-certificates-list">
      <h2 className="font-display text-xl font-bold">Certificates · {items.length}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {items.map((c) => (
          <div
            key={c.id}
            data-testid={`student-certificate-card-${c.id}`}
            className="glass rounded-2xl p-6 flex flex-col"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <div className="text-[10px] tracking-widest font-mono text-brand-cyan uppercase">{c.course_name || 'Course'}</div>
                <div className="font-display font-bold text-lg mt-1 truncate" title={c.batch_name}>{c.batch_name}</div>
              </div>
              <Award className="w-9 h-9 text-brand-cyan shrink-0" />
            </div>
            <dl className="grid grid-cols-2 gap-3 text-xs text-white/70 mb-4">
              <div>
                <dt className="text-white/40">Certificate #</dt>
                <dd className="font-mono text-white truncate">{c.certificate_number || '—'}</dd>
              </div>
              <div>
                <dt className="text-white/40">Issued</dt>
                <dd className="font-mono text-white">{c.issued_date ? c.issued_date.slice(0, 10) : '—'}</dd>
              </div>
            </dl>
            {c.certificate_url ? (
              <a
                href={c.certificate_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`student-certificate-download-${c.id}`}
                className="mt-auto w-full inline-flex items-center justify-center gap-2 bg-brand-cyan text-black font-semibold px-5 py-2.5 rounded-lg btn-glow hover:bg-brand-cyanSoft transition text-sm"
              >
                <Download className="w-4 h-4" /> Download Certificate
              </a>
            ) : (
              <div className="mt-auto text-xs text-white/50 text-center font-mono uppercase tracking-widest">
                Certificate Generated · Awaiting Issuance
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const Profile = ({ student }) => (
  <div className="glass rounded-2xl p-6 max-w-2xl">
    <h3 className="font-display font-bold mb-4">Profile</h3>
    <dl className="space-y-3 text-sm">
      <Row label="Name" value={student.name} />
      <Row label="Email" value={student.email} />
      <Row label="Phone" value={student.phone} />
      <Row label="Batch" value={student.batchName} />
      <Row label="Enrolled" value={student.enrolledOn} />
    </dl>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
    <dt className="text-white/50 text-xs tracking-widest uppercase font-mono">{label}</dt>
    <dd className="font-semibold">{value}</dd>
  </div>
);

export default StudentDashboard;
